import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Megaphone, Filter } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { useToast } from '@/providers/ToastProvider';
import { CampaignTable } from '../components/CampaignTable';
import { adminPartnerService } from '../services/admin-partner.service';
import type { Campaign } from '../types/admin-partner';

export default function AdminCampaignsPage() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'campaigns', page, statusFilter],
    queryFn: () =>
      adminPartnerService.getCampaigns({
        page,
        limit: 10,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      commissionRate: number;
      startDate: string;
      endDate?: string;
    }) => adminPartnerService.createCampaign(data),
    onSuccess: () => {
      toast({ title: 'Campanha criada com sucesso!', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao criar campanha', variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Campaign> }) =>
      adminPartnerService.updateCampaign(id, data),
    onSuccess: () => {
      toast({ title: 'Campanha atualizada com sucesso!', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar campanha', variant: 'error' });
    },
  });

  const handleCreate = async (campaignData: {
    name: string;
    description?: string;
    commissionRate: number;
    startDate: string;
    endDate?: string;
  }) => {
    await createMutation.mutateAsync(campaignData);
  };

  const handleUpdate = async (id: string, campaignData: Partial<Campaign>) => {
    await updateMutation.mutateAsync({ id, data: campaignData });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Campanhas</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Status</SelectItem>
                <SelectItem value="ACTIVE">Ativa</SelectItem>
                <SelectItem value="INACTIVE">Inativa</SelectItem>
                <SelectItem value="EXPIRED">Expirada</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <CampaignTable
        campaigns={data?.data || []}
        isLoading={isLoading}
        total={data?.meta.total || 0}
        page={page}
        pageSize={data?.meta.perPage || 10}
        onPageChange={setPage}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        isCreating={createMutation.isPending}
        isUpdating={updateMutation.isPending}
      />
    </div>
  );
}
